// General-purpose Colombian business-day calculator.
//
// Holidays are computed from Ley Emiliani rules + Easter (Meeus/Jones/Butcher),
// so any year works without maintaining a hand-curated list.
//
// All comparisons are done against the calendar date in America/Bogota, so
// callers can pass Dates in any timezone.

const BOGOTA_TZ = "America/Bogota";

const DATE_KEY_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: BOGOTA_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: BOGOTA_TZ,
  weekday: "short",
});

function toBogotaDateKey(date) {
  return DATE_KEY_FORMATTER.format(date); // "YYYY-MM-DD"
}

function bogotaWeekday(date) {
  return WEEKDAY_FORMATTER.format(date); // "Mon", "Tue", ...
}

// Meeus/Jones/Butcher Gregorian Easter algorithm. Returns {year, month, day}.
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { year, month, day };
}

function ymdKey(year, month, day) {
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

// Add `days` calendar days to {year,month,day}. Uses UTC math to avoid DST drift.
function addDays(ymd, days) {
  const base = Date.UTC(ymd.year, ymd.month - 1, ymd.day);
  const next = new Date(base + days * 86400000);
  return {
    year: next.getUTCFullYear(),
    month: next.getUTCMonth() + 1,
    day: next.getUTCDate(),
  };
}

// Returns a key for the Monday on-or-after the given date (Ley Emiliani).
function mondayOnOrAfter(ymd) {
  const base = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day));
  const dow = base.getUTCDay(); // 0 Sun .. 6 Sat
  const shift = dow === 1 ? 0 : (8 - dow) % 7;
  const shifted = addDays(ymd, shift);
  return ymdKey(shifted.year, shifted.month, shifted.day);
}

const HOLIDAYS_CACHE = new Map();
const HOLIDAY_LIST_CACHE = new Map();

// The statutory table, named, so a settings screen can show what is already
// covered before anyone starts adding days by hand. `moved` marks the twelve
// holidays the Ley Emiliani shifts to the following Monday — the reason a date
// that looks wrong on the calendar is usually right.
export function colombianHolidayList(year) {
  if (HOLIDAY_LIST_CACHE.has(year)) return HOLIDAY_LIST_CACHE.get(year);
  const easter = easterSunday(year);
  const fromYmd = (ymd) => ymdKey(ymd.year, ymd.month, ymd.day);

  const list = [
    { date: ymdKey(year, 1, 1), name: "Año Nuevo", moved: false },
    { date: mondayOnOrAfter({ year, month: 1, day: 6 }), name: "Día de los Reyes Magos", moved: true },
    { date: mondayOnOrAfter({ year, month: 3, day: 19 }), name: "Día de San José", moved: true },
    { date: fromYmd(addDays(easter, -3)), name: "Jueves Santo", moved: false },
    { date: fromYmd(addDays(easter, -2)), name: "Viernes Santo", moved: false },
    { date: ymdKey(year, 5, 1), name: "Día del Trabajo", moved: false },
    { date: mondayOnOrAfter(addDays(easter, 39)), name: "Ascensión del Señor", moved: true },
    { date: mondayOnOrAfter(addDays(easter, 60)), name: "Corpus Christi", moved: true },
    { date: mondayOnOrAfter(addDays(easter, 68)), name: "Sagrado Corazón de Jesús", moved: true },
    { date: mondayOnOrAfter({ year, month: 6, day: 29 }), name: "San Pedro y San Pablo", moved: true },
    { date: ymdKey(year, 7, 20), name: "Día de la Independencia", moved: false },
    { date: ymdKey(year, 8, 7), name: "Batalla de Boyacá", moved: false },
    { date: mondayOnOrAfter({ year, month: 8, day: 15 }), name: "La Asunción de la Virgen", moved: true },
    { date: mondayOnOrAfter({ year, month: 10, day: 12 }), name: "Día de la Raza", moved: true },
    { date: mondayOnOrAfter({ year, month: 11, day: 1 }), name: "Todos los Santos", moved: true },
    { date: mondayOnOrAfter({ year, month: 11, day: 11 }), name: "Independencia de Cartagena", moved: true },
    { date: ymdKey(year, 12, 8), name: "Inmaculada Concepción", moved: false },
    { date: ymdKey(year, 12, 25), name: "Navidad", moved: false },
  ].sort((a, b) => (a.date < b.date ? -1 : 1));

  HOLIDAY_LIST_CACHE.set(year, list);
  return list;
}

// --- institutional calendar --------------------------------------------------
//
// The law is only half the story: an institution also closes on days nobody
// legislated (the full Semana Santa, the week between Christmas and New Year,
// a local fair) and occasionally works a day the law calls a holiday. Both
// live in the general configuration and are applied here, so a term computed
// in the browser agrees with one computed in a cloud function.
let CLOSED_DAYS = new Set();
let OPEN_DAYS = new Set();

// A shape check is not enough: "2026-13-45" matches the pattern and would sit
// in the calendar forever without ever being a day, so the date has to survive
// a round trip through the calendar it claims to be from.
const isRealDayKey = (key) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return false;
  const [y, m, d] = key.split("-").map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y
    && probe.getUTCMonth() === m - 1
    && probe.getUTCDate() === d;
};

const MAX_CALENDAR_DAYS = 2000;

const normalizeDayKeys = (days) => {
  const out = new Set();
  (Array.isArray(days) ? days : []).slice(0, MAX_CALENDAR_DAYS).forEach((d) => {
    const key = typeof d === "string" ? d.trim().slice(0, 10) : "";
    if (isRealDayKey(key)) out.add(key);
  });
  return out;
};

export function setCustomCalendar({ closedDays = [], openDays = [] } = {}) {
  CLOSED_DAYS = normalizeDayKeys(closedDays);
  OPEN_DAYS = normalizeDayKeys(openDays);
}

export function getCustomCalendar() {
  return { closedDays: [...CLOSED_DAYS].sort(), openDays: [...OPEN_DAYS].sort() };
}

export function colombianHolidays(year) {
  if (HOLIDAYS_CACHE.has(year)) return HOLIDAYS_CACHE.get(year);

  const keys = new Set();

  // Fixed-date holidays.
  keys.add(ymdKey(year, 1, 1));   // Año Nuevo
  keys.add(ymdKey(year, 5, 1));   // Día del Trabajo
  keys.add(ymdKey(year, 7, 20));  // Grito de Independencia
  keys.add(ymdKey(year, 8, 7));   // Batalla de Boyacá
  keys.add(ymdKey(year, 12, 8));  // Inmaculada Concepción
  keys.add(ymdKey(year, 12, 25)); // Navidad

  // Emiliani (move to following Monday).
  keys.add(mondayOnOrAfter({ year, month: 1, day: 6 }));   // Reyes Magos
  keys.add(mondayOnOrAfter({ year, month: 3, day: 19 }));  // San José
  keys.add(mondayOnOrAfter({ year, month: 6, day: 29 }));  // San Pedro y San Pablo
  keys.add(mondayOnOrAfter({ year, month: 8, day: 15 }));  // Asunción
  keys.add(mondayOnOrAfter({ year, month: 10, day: 12 })); // Día de la Raza
  keys.add(mondayOnOrAfter({ year, month: 11, day: 1 }));  // Todos los Santos
  keys.add(mondayOnOrAfter({ year, month: 11, day: 11 })); // Independencia de Cartagena

  // Easter-based holidays.
  const easter = easterSunday(year);
  const maundyThursday = addDays(easter, -3);
  const goodFriday = addDays(easter, -2);
  keys.add(ymdKey(maundyThursday.year, maundyThursday.month, maundyThursday.day));
  keys.add(ymdKey(goodFriday.year, goodFriday.month, goodFriday.day));

  // Emiliani Easter-based (move to Monday).
  const ascension = addDays(easter, 39);
  const corpus = addDays(easter, 60);
  const sacredHeart = addDays(easter, 68);
  keys.add(mondayOnOrAfter(ascension));
  keys.add(mondayOnOrAfter(corpus));
  keys.add(mondayOnOrAfter(sacredHeart));

  HOLIDAYS_CACHE.set(year, keys);
  return keys;
}

export function isBusinessDay(date) {
  const key = toBogotaDateKey(date);
  // An explicit working day wins over everything, including a Sunday: that is
  // the only way to undo a mistake in the two lists without a deploy.
  if (OPEN_DAYS.has(key)) return true;
  if (CLOSED_DAYS.has(key)) return false;
  const wd = bogotaWeekday(date);
  if (wd === "Sat" || wd === "Sun") return false;
  const year = parseInt(key.slice(0, 4), 10);
  return !colombianHolidays(year).has(key);
}

// See the note on the cloud-function twin: the exit condition is admin-supplied
// data now, so the walk is bounded.
const MAX_WALK_DAYS = 3660;

export function addBusinessDays(date, n) {
  if (!Number.isFinite(n) || n === 0) return new Date(date.getTime());
  const step = n > 0 ? 1 : -1;
  let remaining = Math.abs(n);
  let cursor = new Date(date.getTime());
  let walked = 0;
  while (remaining > 0) {
    if (walked++ >= MAX_WALK_DAYS) break;
    cursor = new Date(cursor.getTime() + step * 86400000);
    if (isBusinessDay(cursor)) remaining -= 1;
  }
  return cursor;
}

export function diffBusinessDays(a, b) {
  if (a.getTime() === b.getTime()) return 0;
  const forward = a < b;
  const start = forward ? a : b;
  const end = forward ? b : a;
  let count = 0;
  let cursor = new Date(start.getTime());
  while (cursor < end) {
    cursor = new Date(cursor.getTime() + 86400000);
    if (isBusinessDay(cursor)) count += 1;
  }
  return forward ? count : -count;
}
